import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line } from "recharts";
import {
  DollarSign, Users, CheckCircle, XCircle, TrendingUp, Calculator, FileText,
  Eye, Plus, Filter, Download, Search, ArrowRight, ArrowDown, ArrowLeft,
  Zap, Trash2, Loader2, Calendar, Upload, AlertCircle, Settings, Check,
  RefreshCw, Car, LogOut, Lock, Mail, Send, UserCheck, ChevronLeft
} from "lucide-react";
import { API, axios, formatIcelandicNumber, calculatePenaltyInterest, getLoanTypeLabel } from "@/utils/constants";
import { generatePaymentSchedule } from "@/utils/paymentSchedule";
import { useAuth } from "@/context/AuthContext";

// Vanskil Details Component
export const VanskilDetails = () => {
  const navigate = useNavigate();
  const [overduePayments, setOverduePayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ vextir: 17.7, greidslus: 250 });

  useEffect(() => {
    fetchOverduePayments();
  }, []);

  const fetchOverduePayments = async () => {
    try {
      // Fetch all loans
      const loansResponse = await axios.get(`${API}/loans`);
      const loans = loansResponse.data;
      
      // Fetch settings for interest rate calculation
      const settingsResponse = await axios.get(`${API}/settings`);
      setSettings(settingsResponse.data);
      
      // Find all overdue payments
      const overdueList = [];
      loans.forEach(loan => {
        if (loan.scheduled_payments_status) {
          Object.entries(loan.scheduled_payments_status).forEach(([monthNum, status]) => {
            if (status === 'overdue') {
              // Calculate payment amount for this month
              const lantokugjald = loan.lantokugjald ?? Math.max(29900, Math.min(89900, Math.round(loan.loan_amount * 0.089)));
              const amount = loan.loan_amount + lantokugjald;
              const months = loan.requested_term_months;
              const loanRate = loan.interest_rate || settingsResponse.data.vextir || 17.7;
              const rate = loanRate / 100;
              const monthlyRate = rate / 12;
              const monthlyPayment = amount * (monthlyRate * Math.pow(1 + monthlyRate, months)) / (Math.pow(1 + monthlyRate, months) - 1);
              
              // Calculate greidslus (use loan's greidslus or default)
              const greidslus = loan.greidslus || settingsResponse.data.greidslus || 250;
              
              // Calculate base payment amount (without penalty)
              const basePayment = monthlyPayment + greidslus;
              
              // Add penalty interest for overdue payments
              let paymentWithPenalty = basePayment;
              if (loan.payment_schedule) {
                const scheduleEntry = loan.payment_schedule.find(s => s.month === parseInt(monthNum));
                if (scheduleEntry && scheduleEntry.date) {
                  // Parse date from "DD.MM.YYYY" format
                  const dateParts = scheduleEntry.date.split('.');
                  const dueDate = new Date(dateParts[2], dateParts[1] - 1, dateParts[0]);
                  const penaltyRate = settingsResponse.data.drattarvextir || 15.25;
                  const penalty = calculatePenaltyInterest(basePayment, dueDate, penaltyRate);
                  paymentWithPenalty = basePayment + penalty;
                }
              }
              
              overdueList.push({
                loanId: loan.id,
                borrowerName: loan.borrower_info?.full_name || 'N/A',
                borrowerSSN: loan.borrower_info?.ssn || 'N/A',
                borrowerPhone: loan.borrower_info?.phone || 'N/A',
                borrowerEmail: loan.borrower_info?.email || 'N/A',
                loanType: loan.loan_type === 'skuldabref' ? 'Skuldabréf' : 
                         loan.loan_type === 'vedskuldabref' ? 'Veðskuldabréf' : 
                         loan.loan_type === 'kaupleigusamning' ? 'Kaupleigusamningur' : 'Eldra lán',
                monthNumber: parseInt(monthNum),
                basePayment: Math.round(basePayment),
                penaltyInterest: Math.round(paymentWithPenalty - basePayment),
                paymentAmount: Math.round(paymentWithPenalty),
                loanAmount: amount,
                interestRate: loanRate,
                penaltyRate: settingsResponse.data.drattarvextir || 15.25,
                createdAt: loan.created_at
              });
            }
          });
        }
      });
      
      // Sort by month number
      overdueList.sort((a, b) => a.monthNumber - b.monthNumber);
      
      setOverduePayments(overdueList);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching overdue payments:', error);
      toast.error('Ekki tókst að sækja vanskil');
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="p-8">Hleð vanskil...</div>;
  }

  const totalOverdue = overduePayments.reduce((sum, payment) => sum + payment.paymentAmount, 0);
  const totalPenaltyInterest = overduePayments.reduce((sum, payment) => sum + payment.penaltyInterest, 0);
  const totalBasePayment = overduePayments.reduce((sum, payment) => sum + payment.basePayment, 0);

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="outline"
            onClick={() => navigate('/')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Til baka
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Vanskil</h1>
        </div>

        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-600">Fjöldi vanskila</p>
              <p className="text-2xl font-bold text-red-600">{overduePayments.length}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Greiðslur</p>
              <p className="text-2xl font-bold text-gray-700">{formatIcelandicNumber(totalBasePayment)} kr.</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Dráttarvextir</p>
              <p className="text-2xl font-bold text-orange-600">{formatIcelandicNumber(totalPenaltyInterest)} kr.</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Heildarupphæð</p>
              <p className="text-2xl font-bold text-red-600">{formatIcelandicNumber(totalOverdue)} kr.</p>
            </div>
          </div>
        </div>

        {overduePayments.length === 0 ? (
          <Card>
            <CardContent className="p-6 text-center text-gray-500">
              Engin vanskil fundust
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {overduePayments.map((payment, index) => (
              <Card key={index} className="border-l-4 border-l-red-500">
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h3 className="font-semibold text-lg text-gray-900 mb-3">
                        {payment.borrowerName}
                      </h3>
                      <div className="space-y-1.5 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Kennitala:</span>
                          <span className="font-medium">{payment.borrowerSSN}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Sími:</span>
                          <span className="font-medium">{payment.borrowerPhone}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Netfang:</span>
                          <span className="font-medium">{payment.borrowerEmail}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="space-y-1.5 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Tegund láns:</span>
                          <span className="font-medium">{payment.loanType}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Mánuður:</span>
                          <span className="font-medium text-red-600">#{payment.monthNumber}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Greiðsla:</span>
                          <span className="font-medium">{formatIcelandicNumber(payment.basePayment)} kr.</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Dráttarvextir ({payment.penaltyRate}%):</span>
                          <span className="font-medium text-orange-600">{formatIcelandicNumber(payment.penaltyInterest)} kr.</span>
                        </div>
                        <div className="flex justify-between border-t pt-1 mt-1">
                          <span className="text-gray-600 font-semibold">Samtals:</span>
                          <span className="font-bold text-red-600">{formatIcelandicNumber(payment.paymentAmount)} kr.</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Lánsfjárhæð:</span>
                          <span className="font-medium">{formatIcelandicNumber(Math.round(payment.loanAmount))} kr.</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Vextir:</span>
                          <span className="font-medium">{payment.interestRate.toFixed(2)}%</span>
                        </div>
                      </div>
                      <div className="mt-4">
                        <Button
                          size="sm" className="h-7 text-xs px-2"
                          onClick={() => navigate(`/greidslur/${payment.loanId}`)}
                          className="w-full"
                        >
                          Skoða greiðsluáætlun
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

